import map from 'lodash/map';
import xor from 'lodash/xor';

import { OperationStatus } from '@pressreader/appstore';
import { extractIssueVersion } from '@pressreader/content-utils';
import type { CollectionWithId, IssuePageSet } from '@pressreader/social-types';

import {
    createPageSet,
    createPageSetSnippetData,
    getIssuePageSetsApi,
    getPageSetData,
    requestSnippetStatusData,
    updatePageSetData,
    upsertPageSetCollectionsData,
} from './api';
import { mapIssuePageSet } from './mappers';
import { snippetRenderStatusByCode, statusCodes } from './pageset.service.constants';

function getPageSet(pageSetId: string): Promise<IssuePageSet> {
    if (!pageSetId) {
        throw new Error('pageSetId not specified');
    }
    return getPageSetData(pageSetId).then(mapIssuePageSet);
}

/**
 *
 * @param issueKey - a key of the issue
 * @returns - issue page sets
 */
function getIssuePageSets(issueKey: string): Promise<IssuePageSet[]> {
    if (!issueKey) {
        throw new Error('issue not specified');
    }
    return getIssuePageSetsApi(issueKey).then(respData => map(respData.pageSets, mapIssuePageSet));
}

async function findPageSet({ issueKey, pages }: { issueKey: string; pages: number[] }): Promise<IssuePageSet | undefined> {
    const issueVer = extractIssueVersion(issueKey);

    const pageSets: IssuePageSet[] = await getIssuePageSets(issueKey);

    // compare pagesets by equal page numbers in arrays. e.g. - xor([1,2,3], [3, 2, 1, 2]) will return empty array
    const pageSet = pageSets.find(item => xor(item.pages, pages).length === 0 && item.issueVer === issueVer);
    return pageSet;
}

async function createOrGetPageSet({ issueKey, pages }: { issueKey: string; pages: number[] }): Promise<IssuePageSet> {
    const pageSet = await findPageSet({ issueKey: issueKey, pages });
    if (!pageSet) {
        const pageSetCreated = await createPageSet({ issue: issueKey, pages });
        return mapIssuePageSet(pageSetCreated);
    }
    return pageSet;
}

async function createPageSetSnippet({ pageSetId, baseUrl }: { pageSetId: string; baseUrl: string }): Promise<{
    status: OperationStatus;
    url: string;
}> {
    if (!pageSetId) {
        throw new Error('pageSetId not specified');
    }

    const { status, location } = await createPageSetSnippetData({
        baseUrl,
        pageSetId,
    });

    if (status !== statusCodes.accepted && status !== statusCodes.created) {
        throw new Error(`Response with unknown status. Code - ${status}`);
    }

    const returnStatus = snippetRenderStatusByCode[status] ?? OperationStatus.None;

    return {
        status: returnStatus,
        url: location,
    };
}

async function requestSnippetStatus(statusUrl: string): Promise<{ status: OperationStatus; url: string }> {
    const { status, location } = await requestSnippetStatusData(statusUrl);

    if (status !== statusCodes.ok && status !== statusCodes.created) {
        throw new Error(`Response with unknown status. Code - ${status}`);
    }

    return {
        status: snippetRenderStatusByCode[status],
        url: location,
    };
}

function upsertPageSetCollections(pageSetId: IssuePageSet['id'], collections: CollectionWithId[]): Promise<void> {
    return upsertPageSetCollectionsData(pageSetId, { collections });
}

function updatePageSet(pageSetId: string, pages: number[]): Promise<void> {
    return updatePageSetData(pageSetId, { pages });
}

export {
    createOrGetPageSet,
    createPageSet,
    createPageSetSnippet,
    findPageSet,
    getIssuePageSets,
    getPageSet,
    requestSnippetStatus,
    updatePageSet,
    upsertPageSetCollections,
};
